import './feedback.scss';

import { type CSSProperties, useEffect, useState } from 'react';

import { useGlobalContext } from '../../context/global';
import { FEEDBACK_TYPE_COLORS, FEEDBACK_TYPE_LABELS, type IFeedbackSubmission } from '../../interface/feedback';
import { getMyFeedback } from '../../lib/feedback-api';
import { TypeButton } from '../utils/button';
import { Hvc } from '../utils/hooks';
import { FeedbackMenuSVG } from '../utils/svgs';
import FeedbackStatusBadge from './FeedbackStatusBadge';

type LoadState = 'loading' | 'ready' | 'error';

const formatDate = (iso: string) => {
	try {
		return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
	} catch {
		return '';
	}
};

const MyFeedback = () => {
	const { rsProps } = useGlobalContext();
	const [items, setItems] = useState<IFeedbackSubmission[]>([]);
	const [state, setState] = useState<LoadState>('loading');

	const load = () => {
		setState('loading');
		getMyFeedback()
			.then((data) => {
				setItems(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
				setState('ready');
			})
			.catch(() => setState('error'));
	};

	useEffect(load, []);

	const sendNew = () => {
		rsProps?.callSection({ action: 'create', component: 'submit-feedback', title: 'Send Feedback' });
	};

	return (
		<div className='feedback-panel feedback-mine f-column-20'>
			<Hvc
				removeDOM
				view={state === 'loading'}
				className='f-column-12'
			>
				{[0, 1, 2].map((i) => (
					<div
						key={i}
						className='feedback-mine__skeleton skeleton'
					/>
				))}
			</Hvc>

			<Hvc
				removeDOM
				view={state === 'error'}
				className='feedback-mine__empty f-column-12 align-items-center text-center'
			>
				<p className='m-0 text-little color-label'>We couldn’t load your feedback right now.</p>
				<TypeButton
					title='Try again'
					buttonType='outlined'
					buttonSize='little'
					onClick={load}
				/>
			</Hvc>

			<Hvc
				removeDOM
				view={state === 'ready' && items.length === 0}
				className='feedback-mine__empty f-column-15 align-items-center text-center'
			>
				<div className='feedback-mine__empty-icon'>
					<FeedbackMenuSVG fill='var(--primary-500)' />
				</div>
				<div className='f-column-5 align-items-center'>
					<h4 className='m-0'>No feedback yet</h4>
					<p className='m-0 text-little color-label'>Share a bug, an idea, or general feedback about the app.</p>
				</div>
				<TypeButton
					title='Send feedback'
					buttonSize='small'
					onClick={sendNew}
				/>
			</Hvc>

			<Hvc
				removeDOM
				view={state === 'ready' && items.length > 0}
				className='f-column-20'
			>
				<div className='f-row-10 justify-content-between align-items-center'>
					<p className='m-0 text-little color-label'>
						{items.length} submission{items.length !== 1 ? 's' : ''}
					</p>
					<TypeButton
						title='Send new'
						buttonSize='little'
						buttonType='outlined'
						onClick={sendNew}
					/>
				</div>
				<div className='feedback-mine__list f-column-12 stagger-children'>
					{items.map((item) => (
						<div
							key={item.id}
							className='feedback-mine__item'
						>
							<div className='f-row-10 justify-content-between align-items-start'>
								<p className='feedback-mine__item-title m-0'>{item.title}</p>
								<FeedbackStatusBadge status={item.status} />
							</div>
							<p className='feedback-mine__item-desc m-0 text-little color-label'>{item.description}</p>
							<div className='f-row-12 align-items-center feedback-mine__item-meta'>
								<span
									className='feedback-mine__item-type'
									style={{ '--accent': FEEDBACK_TYPE_COLORS[item.type] } as CSSProperties}
								>
									{FEEDBACK_TYPE_LABELS[item.type]}
								</span>
								<span className='text-tiny color-label'>{formatDate(item.createdAt)}</span>
							</div>
							<Hvc
								removeDOM
								view={!!item.adminResponse}
								className='feedback-mine__response'
							>
								<p className='m-0 text-tiny feedback-mine__response-label'>Response from the team</p>
								<p className='m-0 text-little'>{item.adminResponse}</p>
							</Hvc>
						</div>
					))}
				</div>
			</Hvc>
		</div>
	);
};

export default MyFeedback;
